import React, { useState, useRef, useEffect } from 'react';
import { CloudArrowUpIcon, MicrophoneIcon, CheckCircleIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

export default function VoiceClone() {
  const { user } = useAuth();
  const [currentVoice, setCurrentVoice] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Recording/Upload state
  const [mode, setMode] = useState('upload'); // 'upload' or 'record'
  const [audioFile, setAudioFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    loadCurrentVoice();
  }, [user]);

  const loadCurrentVoice = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/podcast/progress/${user.id}`);
      console.log('Voice data:', response.data);
      
      if (response.data.session && response.data.session.voice_id) {
        setCurrentVoice({
          voice_id: response.data.session.voice_id,
          preview_audio_url: response.data.session.preview_audio_url
        });
      } else {
        setCurrentVoice(null);
      }
    } catch (err) {
      console.log('No existing voice found:', err);
      setCurrentVoice(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('audio/')) {
        setError('Please select an audio file');
        return;
      }
      setAudioFile(file);
      setError('');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          if (newTime >= 300) {
            stopRecording();
          }
          return newTime;
        });
      }, 1000);

    } catch (err) {
      setError('Failed to access microphone. Please check permissions.');
      console.error('Recording error:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const handleSubmit = async () => {
    setIsProcessing(true);
    setError('');

    try {
      const formData = new FormData();
      
      if (mode === 'upload' && audioFile) {
        formData.append('audio_file', audioFile);
      } else if (mode === 'record' && recordedBlob) {
        formData.append('audio_file', recordedBlob, 'recording.webm');
      } else {
        setError('Please provide an audio file');
        setIsProcessing(false);
        return;
      }

      if (!user) {
        setError('Please log in to clone your voice');
        setIsProcessing(false);
        return;
      }
      
      formData.append('user_id', user.id);

      const response = await axios.post(`${API_URL}/voice/clone`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Reload current voice
      await loadCurrentVoice();
      
      // Reset form
      setAudioFile(null);
      setRecordedBlob(null);
      setRecordingTime(0);
      
      alert(`✅ Voice cloned successfully!\n\nVoice ID: ${response.data.voice_id}`);

    } catch (err) {
      console.error('Voice cloning error:', err);
      setError(err.response?.data?.detail || 'Failed to clone voice. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const togglePreview = () => {
    if (!audioRef.current) return;
    
    if (isPlayingPreview) {
      audioRef.current.pause();
      setIsPlayingPreview(false);
    } else {
      audioRef.current.play();
      setIsPlayingPreview(true);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-8 mb-8 border border-purple-100">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <MicrophoneIcon className="h-12 w-12 text-purple-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Preserve Your Voice Forever</h1>
            <p className="text-lg text-gray-700 mb-4">
              Your voice is more than just sound—it's comfort, love, and connection. Clone your voice so your loved ones can hear you speak to them, even when you can't be there in person.
            </p>
            
            <div className="bg-white/60 backdrop-blur rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Why clone your voice?</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">💝</span>
                  <span>Every message sounds like you—warm, personal, and authentic</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">🎯</span>
                  <span>AI generates responses in your voice when loved ones reach out</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">⏰</span>
                  <span>Takes just 2 minutes—record or upload a short audio sample</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Current Voice Status - Show YOUR voice sample */}
      {currentVoice && currentVoice.voice_id && (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-8 mb-8">
          <div className="flex items-start justify-between">
            <div className="flex items-start flex-1">
              <CheckCircleIcon className="h-12 w-12 text-green-600 mr-4 flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">✨ Your Voice is Active</h2>
                <p className="text-gray-700 mb-4">
                  Your loved ones can now hear your voice in every message. It's you—your warmth, your tone, your love—preserved forever.
                </p>
                
                {/* Voice Quality Indicator */}
                <div className="bg-white rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-700">Voice Quality</span>
                    <span className="text-xs text-gray-500">Based on sample length</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{width: '60%'}}></div>
                    </div>
                    <span className="text-sm font-medium text-gray-700">Good</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    💡 Tip: Record a longer sample (3-5 minutes) for even better quality and more natural conversations
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* YOUR Voice Sample - This is what loved ones hear */}
          <div className="mt-6 pt-6 border-t border-green-200">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                  <PlayIcon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">🎧 Hear Your Cloned Voice</h3>
                  <p className="text-sm text-gray-700">
                    This is a sample message in YOUR voice. This is exactly what your loved ones will hear when they receive messages from you.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 mb-3">
                <p className="text-sm font-medium text-gray-700 mb-3">
                  📝 Sample Message: "Hi sweetheart, I'm so proud of you. Remember, I'm always here for you, no matter what."
                </p>
                <audio controls className="w-full" style={{height: '45px'}}>
                  {currentVoice.preview_audio_url && (
                    <source src={currentVoice.preview_audio_url} type="audio/mpeg" />
                  )}
                  Your browser does not support audio playback.
                </audio>
              </div>

              <div className="flex items-start gap-2 text-xs text-gray-600 bg-blue-50 p-3 rounded-lg">
                <span>💡</span>
                <p>
                  <strong>How it works:</strong> When your loved ones reach out, our AI generates personalized responses in YOUR voice, making every message feel like it's really coming from you.
                </p>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 font-mono bg-white px-3 py-1 rounded inline-block mt-4">
            Voice ID: {currentVoice.voice_id}
          </p>
        </div>
      )}

      {/* Improve Voice Section - Shows when voice exists */}
      {currentVoice && currentVoice.voice_id && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">🎯</span>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Improve Your Voice Quality</h2>
              <p className="text-gray-600">
                Want your voice to sound even more natural? Record a longer sample with varied emotions and expressions.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-3xl mb-2">📏</div>
              <h3 className="font-semibold text-gray-900 mb-1">Longer Sample</h3>
              <p className="text-sm text-gray-600">3-5 minutes captures more nuances</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-3xl mb-2">🎭</div>
              <h3 className="font-semibold text-gray-900 mb-1">Varied Emotions</h3>
              <p className="text-sm text-gray-600">Happy, sad, excited, calm</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-3xl mb-2">💬</div>
              <h3 className="font-semibold text-gray-900 mb-1">Natural Speech</h3>
              <p className="text-sm text-gray-600">Tell stories, share memories</p>
            </div>
          </div>

          <button
            onClick={() => setCurrentVoice(null)}
            className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
          >
            🎙️ Record Improved Sample
          </button>
        </div>
      )}

      {/* Clone New Voice Section */}
      {(!currentVoice || !currentVoice.voice_id) && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {currentVoice ? 'Record New Sample' : 'Let\'s Capture Your Voice'}
          </h2>
          <p className="text-gray-600 mb-6">
            {currentVoice 
              ? 'Record a longer, more expressive sample to improve voice quality.' 
              : 'Choose how you\'d like to share your voice with us. Either way takes just a couple of minutes.'}
          </p>

          {/* Mode Selector */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setMode('upload')}
              className={`flex-1 py-4 px-6 rounded-xl font-semibold transition-all ${
                mode === 'upload'
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <CloudArrowUpIcon className="h-6 w-6 mx-auto mb-2" />
              Upload Audio
            </button>
            <button
              onClick={() => setMode('record')}
              className={`flex-1 py-4 px-6 rounded-xl font-semibold transition-all ${
                mode === 'record'
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <MicrophoneIcon className="h-6 w-6 mx-auto mb-2" />
              Record Now
            </button>
        </div>

        {/* Upload Mode */}
        {mode === 'upload' && (
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
            <input
              type="file"
              accept="audio/*"
              onChange={handleFileSelect}
              className="hidden"
              id="audio-upload"
            />
            <label htmlFor="audio-upload" className="cursor-pointer block">
              <CloudArrowUpIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-semibold text-gray-700 mb-2">
                Click to upload audio file
              </p>
              <p className="text-sm text-gray-500">
                MP3, WAV, M4A, or any audio format (min 1 minute)
              </p>
            </label>
            {audioFile && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <p className="text-sm font-medium text-green-800">
                  ✓ {audioFile.name}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {(audioFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            )}
          </div>
        )}

        {/* Record Mode */}
        {mode === 'record' && (
          <div className="border-2 border-gray-300 rounded-xl p-8">
            <div className="text-center mb-6">
              <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4 ${
                isRecording ? 'bg-red-100 animate-pulse' : 'bg-purple-100'
              }`}>
                <MicrophoneIcon className={`h-12 w-12 ${
                  isRecording ? 'text-red-600' : 'text-purple-600'
                }`} />
              </div>
              
              <div className="text-4xl font-bold text-gray-900 mb-2">
                {formatTime(recordingTime)}
              </div>
              
              {recordingTime < 60 && isRecording && (
                <p className="text-sm text-orange-600 font-medium">
                  Keep recording... (minimum 1 minute)
                </p>
              )}
              
              {recordingTime >= 60 && isRecording && (
                <p className="text-sm text-green-600 font-medium">
                  ✓ Great! You can stop now or keep going
                </p>
              )}
            </div>

            <div className="flex gap-4">
              {!isRecording && !recordedBlob && (
                <button
                  onClick={startRecording}
                  className="flex-1 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all"
                >
                  Start Recording
                </button>
              )}
              
              {isRecording && (
                <button
                  onClick={stopRecording}
                  disabled={recordingTime < 60}
                  className={`flex-1 py-3 rounded-xl font-semibold transition-all ${
                    recordingTime >= 60
                      ? 'bg-gray-800 text-white hover:bg-gray-900'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Stop Recording
                </button>
              )}
              
              {recordedBlob && !isRecording && (
                <>
                  <button
                    onClick={() => {
                      setRecordedBlob(null);
                      setRecordingTime(0);
                    }}
                    className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300"
                  >
                    Record Again
                  </button>
                  <div className="flex-1 p-3 bg-green-50 rounded-xl text-center">
                    <p className="text-sm font-medium text-green-800">
                      ✓ Recording ready ({formatTime(recordingTime)})
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Recording Tips */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 mb-2">💡 Tips for best results:</p>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Speak naturally, like you're talking to a loved one</li>
                <li>• Record in a quiet environment</li>
                <li>• Vary your tone - read a story, share memories, express emotions</li>
                <li>• Minimum 1 minute, but 2-3 minutes is better for quality</li>
              </ul>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <div className="mt-6">
          <button
            onClick={handleSubmit}
            disabled={isProcessing || (!audioFile && !recordedBlob) || (mode === 'record' && recordingTime < 60)}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
              isProcessing || (!audioFile && !recordedBlob) || (mode === 'record' && recordingTime < 60)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl'
            }`}
          >
            {isProcessing ? 'Cloning Voice...' : currentVoice?.voice_id ? 'Update Voice Clone' : 'Create Voice Clone'}
          </button>
        </div>
        </div>
      )}
    </div>
  );
}
